'use client';
import { GREY_1000, SHADOW_3, TEXT_FONT } from '@mnd-frontend/ui/shared/tokens';
import { memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHoveredTickContext, useMouseContext, useSvgContext } from '../../context';
import TooltipBody from './TooltipBody';

export const PositionRelative = styled.div`
  position: relative;
`;

const TooltipStyle = styled.div<{ $show: boolean }>`
  color: ${GREY_1000};
  font-family: ${TEXT_FONT};
  font-size: 1rem;
  font-weight: normal;
  line-height: 1.25;
  box-shadow: ${SHADOW_3};
  background-color: white;
  border-radius: 0.25rem;
  opacity: ${props => (props.$show ? 1.0 : 0.0)};
  margin: 0;
  padding: 0.75rem 1rem;
  transition:
    opacity 0.3s,
    top 0.5s,
    left 0.5s;
  position: absolute;
  pointer-events: none;
`;

const ChartTooltip = memo(({ data = [], dataSeries = [], totalText, scaleX, ...props }: any) => {
  const { mouseY } = useMouseContext();
  const hoveredTick = useHoveredTickContext();
  const { width, height } = useSvgContext();
  const [{ offsetWidth, offsetHeight }, setTooltipSize] = useState<any>({});
  const [{ tooltipX, tooltipY }, setTooltipCoords] = useState({ tooltipX: 1, tooltipY: 1 });

  const dataPoint = hoveredTick !== undefined && data[hoveredTick];
  const mouseX = scaleX?.(hoveredTick);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No idea why
  const measuredRef = useCallback(
    (node: any) => {
      if (node !== null) {
        const bbox = node.getBoundingClientRect();
        setTooltipSize({
          offsetWidth: bbox.width, // node.offsetWidth,
          offsetHeight: bbox.height, // node.offsetHeight,
        });
      }
    },
    [dataPoint],
  );

  useEffect(() => {
    if (mouseX && mouseY) {
      const sgnX = Math.sign(width - 2 * mouseX);
      const tooltipXRaw = mouseX + sgnX * 10 + ((sgnX - 1) / 2) * offsetWidth;
      setTooltipCoords({
        tooltipX: Math.min(width - offsetWidth, Math.max(0, tooltipXRaw)),
        tooltipY: ((mouseY + 10) / (height + 20)) * (height - offsetHeight),
      });
    }
  }, [mouseX, mouseY, width, height, offsetWidth, offsetHeight]);

  const show = !!(dataPoint && dataPoint.name);

  return (
    <TooltipStyle
      ref={measuredRef}
      $show={show}
      style={{
        left: tooltipX ? `${tooltipX}px` : undefined,
        top: tooltipY ? `${tooltipY}px` : undefined,
      }}
    >
      <TooltipBody
        show={show}
        dataPoint={dataPoint}
        dataSeries={dataSeries}
        totalText={totalText}
        {...props}
      />
    </TooltipStyle>
  );
});

export default ChartTooltip;
